import { TTranslator } from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import {
  EDuration,
  ETariffType,
  ICostMap,
  INormalizedTariff,
  IServerTariff,
} from 'types/tariff';
import {
  assumeDuration,
  getSeconds,
  secondsToWeekDays,
  weekDaysToSeconds,
} from 'utils';

export const getCosts = (
  costMapArray: Array<ICostMap>
): { cost: number; user_cost: number } => {
  const userCostObject = costMapArray.find(
    (item) => item.title === 'USER_COST'
  );

  const cost =
    costMapArray.find((item) => item.title === 'DEFAULT')?.value ?? 0;
  const user_cost = userCostObject?.value ?? cost;

  return { cost, user_cost };
};

export const normalizeTariff = (
  tariff: IServerTariff,
  t: TTranslator<TLocalesKeys>
): INormalizedTariff => {
  const {
    has_fixed_finish_time,
    use_global_discounts,
    title,
    is_active,
    duration,
    schedules,
    price_list,
    per_minute,
    show_in_shell,
    show_in_billing,
    pausable,
    lifetime,
    sell_schedules,
    highlighted,
    ean,
    sort,
    is_tracked,
    online_booking_enabled,
    use_on_holidays,
    disable_sale_receipt_printing,
  } = tariff;

  const { interval, value } = assumeDuration(duration, t) ?? {};

  const assumedLifetime = assumeDuration(lifetime ?? 60, t);

  return {
    is_tracked,
    use_on_holidays,
    use_global_discounts,
    highlighted,
    show_in_shell,
    show_in_billing,
    title,
    type: per_minute
      ? ETariffType.PER_MINUTE
      : has_fixed_finish_time
      ? ETariffType.PACKET
      : pausable
      ? ETariffType.PAUSABLE
      : ETariffType.FIXED,
    is_active,
    duration: value ?? '',
    duration_type: interval ?? EDuration.minutes,
    lifetime: assumedLifetime?.value ?? '',
    lifetime_type: assumedLifetime?.interval ?? EDuration.minutes,
    days: schedules.map((schedule) => secondsToWeekDays(schedule.start).day),
    hours: {
      start: secondsToWeekDays(schedules[0].start).hour,
      end: secondsToWeekDays(schedules[0].end).hour,
    },
    sales_hours: {
      start: secondsToWeekDays(Number(sell_schedules?.[0]?.start ?? 0)).hour,
      end: secondsToWeekDays(Number(sell_schedules?.[0]?.end ?? 0)).hour,
    },
    price_list: price_list.map((item) => ({
      host_groups: item.host_groups.map((group) => group.id),
      ...getCosts(
        item.cost_map.filter(
          (item) => item.title === 'DEFAULT' || item.title === 'USER_COST'
        )
      ),
    })),
    ean: ean ?? '',
    sort,
    online_booking_enabled,
    disable_sale_receipt_printing,
  };
};

const ALL_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7];

export const prepareBeforeMutation = (
  values: INormalizedTariff
): Omit<IServerTariff, 'id'> => {
  const {
    use_global_discounts,
    title,
    is_active,
    duration,
    duration_type,
    price_list,
    days,
    hours,
    type,
    show_in_shell,
    show_in_billing,
    lifetime,
    lifetime_type,
    sales_hours,
    highlighted,
    ean,
    sort,
    is_tracked,
    online_booking_enabled,
    use_on_holidays,
    disable_sale_receipt_printing,
  } = values;

  const validatedDays = days ?? ALL_WEEK_DAYS;
  const validatedHours = hours?.start
    ? hours
    : { start: '00:00', end: '00:00' };

  const schedules = weekDaysToSeconds(validatedHours, validatedDays);
  const sell_schedules = weekDaysToSeconds(
    { ...validatedHours, end: sales_hours.end || validatedHours.end },
    validatedDays
  );

  return {
    use_on_holidays,
    is_tracked,
    online_booking_enabled,
    sort,
    has_fixed_finish_time: type === ETariffType.PACKET,
    use_global_discounts,
    highlighted,
    per_minute: type === ETariffType.PER_MINUTE,
    show_in_billing,
    pausable: type === ETariffType.PAUSABLE,
    title,
    is_active,
    duration:
      type === ETariffType.PACKET
        ? 1
        : getSeconds(duration, duration_type as string) ?? 1,
    lifetime:
      type === ETariffType.PAUSABLE
        ? getSeconds(lifetime, lifetime_type as string) ?? 1
        : undefined,
    schedules,
    sell_schedules:
      type === ETariffType.PAUSABLE || type === ETariffType.PACKET
        ? sell_schedules
        : undefined,
    show_in_shell,
    disable_sale_receipt_printing,
    //@ts-ignore TODO: Нужно разделить типы
    price_list: price_list.map((item) => ({
      host_groups: item.host_groups?.map((groupId) => groupId),
      cost_map: [
        {
          title: 'DEFAULT',
          value: item.cost,
          currency: 'rub',
        },
        {
          title: 'USER_COST',
          value: item.user_cost === '' ? item.cost : item.user_cost,
          currency: 'rub',
        },
      ],
    })),
    ean,
  };
};

/**
 * Определяет есть ли во времени работы тарифа переход на сл. сутки
 *
 * @param startTime время начала в формате HH:mm
 * @param endTime время окончания в формате HH:mm
 */
export const checkIsMultipleDays = (
  startTime: string,
  endTime: string
): boolean => {
  if (startTime.length !== 5 || endTime.length !== 5) {
    return false;
  }

  if (startTime === endTime) {
    return true;
  }

  const [startHours, startMinutes] = startTime.split(':');
  const [endHours, endMinutes] = endTime.split(':');

  return !(
    startHours < endHours ||
    (startHours === endHours && startMinutes < endMinutes)
  );
};
